import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Knowledge Base - Common Issues & Troubleshooting
 */
const CommonIssuesPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const commonIssues = [
    {
      category: "Login & Authentication",
      issues: [
        {
          question: "I can't log in to my account",
          answer: "Try these steps: 1) Clear your browser cache and cookies. 2) Ensure you're using the correct email address. 3) Try the 'Forgot Password' link to reset your password. 4) If using SSO, contact your IT administrator. 5) Contact support if issues persist."
        },
        {
          question: "My session keeps timing out",
          answer: "For security, sessions expire after 30 minutes of inactivity. To avoid losing work: 1) Save your work frequently. 2) Check your browser's security settings aren't blocking cookies. 3) Contact your administrator if you need longer session times."
        },
        {
          question: "MFA code isn't working",
          answer: "Ensure your device's time is synchronized correctly. If using an authenticator app, verify you're using the code for Videxa (not another service). Try generating a new code. Contact support to reset MFA if needed."
        }
      ]
    },
    {
      category: "Data & Reports",
      issues: [
        {
          question: "My data isn't showing up",
          answer: "Data may take up to 15 minutes to sync from connected systems. Check: 1) Your integration status in Settings. 2) The date range filter on your view. 3) Your department/facility filters. 4) Contact support if data is older than 24 hours."
        },
        {
          question: "Report is showing incorrect numbers",
          answer: "Verify: 1) The date range is correct. 2) All applicable filters are set properly. 3) The report parameters match your expectations. 4) If using comparisons, ensure the comparison period is valid. Contact support with specific examples."
        },
        {
          question: "Export to Excel isn't working",
          answer: "Ensure pop-ups are allowed for our site. Try a different browser if issues persist. For large exports, use the 'Schedule Export' feature to receive the file via email. Check that your Excel version supports the export format."
        }
      ]
    },
    {
      category: "Claims & Billing",
      issues: [
        {
          question: "Claim submission is failing",
          answer: "Check the error message for specific details. Common causes: 1) Missing required fields. 2) Invalid NPI or tax ID. 3) Clearinghouse connectivity issues. 4) Payer enrollment problems. Review the claim edit report and correct flagged items."
        },
        {
          question: "ERA/remittance not posting automatically",
          answer: "Verify: 1) Remittance mapping is configured correctly. 2) The ERA file format matches your settings. 3) Bank account information is current. Check the remittance exception queue for items requiring manual review."
        },
        {
          question: "Denial codes aren't translating correctly",
          answer: "We update denial code mappings regularly. If you see unmapped codes: 1) Check the code in your denial dashboard. 2) Report unmapped codes to support. 3) We'll add the mapping within 24 hours."
        }
      ]
    },
    {
      category: "Integration",
      issues: [
        {
          question: "EHR integration is disconnected",
          answer: "Check: 1) Your EHR system status. 2) Network connectivity between systems. 3) API credentials haven't expired. Go to Settings > Integrations to view status and re-authenticate if needed. Contact your IT team if firewall changes were made."
        },
        {
          question: "Data sync is delayed",
          answer: "Normal sync occurs every 15 minutes. Delays may occur due to: 1) High system volume. 2) Source system maintenance. 3) Network issues. Check the sync status dashboard. Contact support if delays exceed 2 hours."
        },
        {
          question: "Duplicate records appearing",
          answer: "Duplicates may occur from: 1) Multiple source systems. 2) Manual entry alongside integration. 3) Patient matching issues. Use the duplicate management tool in Settings to merge records. Contact support for bulk duplicate resolution."
        }
      ]
    }
  ];

  const toggleIssue = (categoryIndex, issueIndex) => {
    const key = `${categoryIndex}-${issueIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="common-issues-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0E3056] to-[#1a4a7a] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/knowledge-base" className="text-blue-300 hover:text-white mb-4 inline-block">
              ← Back to Knowledge Base
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Common Issues</h1>
            <p className="text-xl text-gray-200">
              Find solutions to frequently encountered problems.
            </p>
          </div>
        </div>
      </section>

      {/* Issues by Category */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {commonIssues.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.issues.map((issue, issueIndex) => {
                    const key = `${categoryIndex}-${issueIndex}`;
                    const isOpen = openIndex === key;
                    return (
                      <div
                        key={issueIndex}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleIssue(categoryIndex, issueIndex)}
                          className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <span className="font-medium text-gray-900 dark:text-white">
                            {issue.question}
                          </span>
                          <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                        </button>
                        {isOpen && (
                          <div className="p-4 pt-0 border-t border-gray-200 dark:border-gray-600">
                            <p className="text-gray-600 dark:text-gray-300">
                              {issue.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
            Still Need Help?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-[#0E3056] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#1a4a7a] transition-colors"
            >
              Contact Support
            </Link>
            <Link
              to="/knowledge-base/error-codes"
              className="inline-block border-2 border-[#0E3056] text-[#0E3056] dark:border-[#FFC000] dark:text-[#FFC000] px-8 py-3 rounded-lg font-semibold hover:bg-[#0E3056] hover:text-white dark:hover:bg-[#FFC000] dark:hover:text-gray-900 transition-colors"
            >
              View Error Codes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommonIssuesPage;
